import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import ElementLoader from "components/ElementLoader";
import Widget from "components/Layout/Widget";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { buildCommentDeepLinkPath } from "containers/Resources/screens/ResourcesSingle/components/CommentsPanel/commentPermalink";
import {
    isRecordReadOnly,
    ReadOnlyFlags,
    resolveToggleAction,
} from "containers/Resources/screens/ResourcesSingle/components/CommentsPanel/commentResolve";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getTypedLowerCase } from "utils/service";
import { buildAssignedCommentsFilter, commentSnippet } from "./assignedComments";
import "./AssignedComments.scss";

const LIMIT = 5;

type RecordLabelIconType = Parameters<typeof RecordLabel>[0]["iconByType"];

interface AssignedCommentRow {
    id: string;
    body: string;
    created_at: string;
    resolved_at: string | null;
    record_id: string;
    author_identity_id_identity: { name: string } | null;
    record_id_record: ({ name: string; record_type: string } & ReadOnlyFlags) | null;
}

const QUERY = `query assigned_comments($filter: String, $limit: Int) {
    record_comment_plain_list(filter: $filter, order: "-created_at", limit: $limit) {
        id
        body
        created_at
        resolved_at
        record_id
        author_identity_id_identity { name }
        record_id_record { name record_type is_readonly is_project_readonly is_bundle_readonly }
    }
}`;

/**
 * Workspace dashboard widget: the open comments assigned to the viewer, newest
 * first. Each row links to the comment on its record and carries the same
 * "Mark as done" checkmark as the comments panel, so the list works as a to-do
 * list (Margot, 2026-09-29).
 */
export const AssignedComments = () => {
    const userData = useSelector(getUserData);
    const identityId: string | undefined = userData?.identity_id;
    const [rows, setRows] = useState<AssignedCommentRow[] | null>(null);
    const [resolvingId, setResolvingId] = useState<string | null>(null);

    const fetchRows = useCallback(() => {
        if (!identityId) {
            return;
        }
        restClient
            .post("/graphql", {
                query: QUERY,
                variables: { filter: buildAssignedCommentsFilter(identityId), limit: LIMIT },
            })
            .then((response) => {
                const list: (AssignedCommentRow | null)[] =
                    response?.data?.record_comment_plain_list ?? [];
                setRows(list.filter((row): row is AssignedCommentRow => !!row));
            })
            .catch(() => setRows([]));
    }, [identityId]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const markDone = useCallback(
        (row: AssignedCommentRow) => {
            if (resolvingId) {
                return;
            }
            const action = resolveToggleAction(row.resolved_at);
            setResolvingId(row.id);
            restClient
                .post("/graphql", {
                    query: `mutation update_record_comment($id: String!, $resolved_at: String) {
                        update_record_comment(id: $id, body: {resolved_at: $resolved_at}) { id }
                    }`,
                    variables: { id: row.id, resolved_at: action.nextResolvedAt },
                })
                .then(() => fetchRows())
                .catch((error) => {
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't mark the comment as done"
                            error={error}
                        />,
                    );
                })
                .finally(() => setResolvingId(null));
        },
        [resolvingId, fetchRows],
    );

    if (rows === null) {
        return <ElementLoader />;
    }
    if (rows.length === 0) {
        return <Widget.Empty />;
    }

    return (
        <div>
            {rows.map((row) => {
                const recordType = getTypedLowerCase(
                    row.record_id_record?.record_type ?? "",
                ) as FeConfigType;
                const action = resolveToggleAction(row.resolved_at);
                return (
                    <div className="assignedComments-item" key={row.id}>
                        <div className="assignedComments-item__head">
                            <span className="text-sm text-dark-grey">
                                {row.author_identity_id_identity?.name ?? "Unknown"} ·{" "}
                                <TimeAgo datetime={row.created_at} />
                            </span>
                            {isRecordReadOnly(row.record_id_record) ? (
                                <span
                                    className="assignedComments-item__readonly text-sm text-dark-grey"
                                    title="The record is read-only, so this comment cannot be marked as done."
                                >
                                    Read-only
                                </span>
                            ) : (
                                <Button
                                    icon={action.icon}
                                    variation="icon"
                                    title={action.title}
                                    ariaLabel={action.ariaLabel}
                                    onClick={() => markDone(row)}
                                    loading={resolvingId === row.id}
                                    labelOnlyAria
                                />
                            )}
                        </div>
                        <div className="assignedComments-item__label">
                            <RecordLabel
                                name={row.record_id_record?.name ?? row.record_id}
                                link={buildCommentDeepLinkPath(recordType, row.record_id, row.id)}
                                iconByType={recordType as RecordLabelIconType}
                            />
                        </div>
                        <div className="assignedComments-item__snippet">
                            {commentSnippet(row.body)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AssignedComments;
